/**
 * Extract a value from the Confluence `Content.extensions` array.
 *
 * The AGG schema has NO standalone `type Attachment` — attachments are
 * `Content` nodes with `type: "attachment"`. File metadata like `fileSize`
 * and `mediaType` lives inside the generic `extensions: [KeyValueHierarchyMap]`
 * array rather than as first-class fields.
 *
 * See PROGRESS.md §27.1–27.2 and GRAPHQL_PLAN.md §0.3 for the schema rationale.
 *
 * Mirrors `confluence/next/packages/attachments-page/src/attachmentUtils.ts:27`
 * but with stricter null-tolerance to match the generated Relay types
 * (`{ readonly key: string | null; readonly value: string | null } | null`).
 *
 * @example
 *   const fileSize = Number(getExtensionValue(node.extensions, 'fileSize') ?? 0);
 *   const mediaType = getExtensionValue(node.extensions, 'mediaType') ?? '';
 */

export type ExtensionKeyValue = {
	readonly key: string | null;
	readonly value: string | null;
} | null;

/**
 * Returns the `value` from the first `extensions` entry matching `key`, or
 * `null` if not found. Tolerant of null/undefined inputs.
 */
export const getExtensionValue = (
	extensions: ReadonlyArray<ExtensionKeyValue> | null | undefined,
	key: string,
): string | null => {
	if (!extensions) {
		return null;
	}
	const entry = extensions.find((e) => e?.key === key);
	return entry?.value ?? null;
};
